/**
 * FALL PHYSICS — dedicated Matter.js world for the fall stage.
 *
 * Responsibilities:
 *  - Flask static bodies: mouth rim frame (`flask_mouth`) + neck walls (`flask_neck`) + body trapezoid (`flask_wall`).
 *  - Per-object body creation: boulder (elliptic), gem (circle), bonus (circle),
 *    merge_pickup (tier-based ellipse without FLASK_MERGE_SCALE).
 *  - Collision events: boulder → flask wall calls onBoulderHit (FallPhase emits fall:boulder); other
 *    objects (including debris) → flask wall emits fall:bounce (with per-object cooldown).
 *  - Collectibles are collected ONLY at the mouth (geometry collect band + `checkSuction`).
 *    Flask shell never collects; sucked objects are frozen, animated by FallRenderer,
 *    then removed after FALL_SUCTION_ANIMATION_MS.
 *  - Off-screen pruning for bodies that fall below the run area.
 *
 * Objects do NOT collide with each other (category/mask collision filters).
 */
import Matter from "matter-js";
import * as C from "../config/constants.js";
import {
  computeFallDrawTopOverflowPx,
  fallObjectPixelWidth,
  getFallGravityForRunHeight,
  getFallObjectDrawSize,
  getFallSpawnTopMarginPx,
} from "../config/constants.js";
import { EventBus } from "../core/event-bus.js";
import { createFallSpawnBurstParticles } from "./merge-particles.js";
import { gameImageUrl } from "../assets/game-image-urls.js";
import { getCachedImage } from "../assets/image-cache.js";
import {
  fallCollectSlabQualifiesForCollection,
  getFallCollectSlabTuning,
  layoutFallCollectSlab,
  layoutFallMouth,
  layoutFallMouthLid,
} from "./fall-collect-slab.js";
import {
  buildRunwayFlaskPhysicsGeometry,
  createRunwayFlaskMatterColliders,
  getFallMouthHalfWidthPx,
  getRunwayFlaskPlacement,
} from "./fall-flask-shape.js";

var DEFAULT_GRAVITY = 1.0;
  var DEFAULT_SUCTION_RADIUS_RATIO = 1.1;
  var DEFAULT_SUCTION_MS = 50;
  var DEFAULT_WALL_THICKNESS = 28;
  var BOUNCE_COOLDOWN_MS = 500;
  var DEFAULT_PHYSICS_STEP_MS = 1000 / 60;
  var DEFAULT_MAX_SUBSTEPS = 6;
  var DEFAULT_MAX_FRAME_MS = 100;

  function _getDebrisImg() {
    return getCachedImage(gameImageUrl("boulder"));
  }

  // Collision categories (Matter.js: pair collides when both (A.cat & B.mask) and (B.cat & A.mask))
  var CAT_BOULDER       = 0x0001; // boulder objects
  var CAT_FLASK_SHELL   = 0x0002; // neck rect + body trapezoid — rocks + collectibles
  var CAT_OTHER_OBJ     = 0x0004; // gems, bonuses, merge pickups
  var CAT_FLASK_MOUTH   = 0x0008; // mouth rim sensor + mouth-lid bar (lid: boulders + debris only)
  var CAT_DEBRIS        = 0x0010; // boulder fragments

  var MASK_FLASK_SHELL = CAT_BOULDER | CAT_OTHER_OBJ | CAT_DEBRIS;
  var MASK_FLASK_MOUTH = CAT_BOULDER | CAT_DEBRIS;
  var MASK_COLLECTIBLE_FLASK = CAT_FLASK_SHELL;

  // ---- helpers ----

  /** `runConfig` + legacy `FALL_COLLECT_SLAB_*` keys from config/constants.js. */
  function collectSlabRunConfig(runConfig) {
    var rc = runConfig || {};
    var out = Object.assign({}, rc);
    if (out.FALL_COLLECT_SLAB_HEIGHT_PX == null && C.FALL_COLLECT_SLAB_HEIGHT_PX != null) {
      out.FALL_COLLECT_SLAB_HEIGHT_PX = C.FALL_COLLECT_SLAB_HEIGHT_PX;
    }
    if (out.FALL_COLLECT_SLAB_Y_OFFSET_PX == null && C.FALL_COLLECT_SLAB_Y_OFFSET_PX != null) {
      out.FALL_COLLECT_SLAB_Y_OFFSET_PX = C.FALL_COLLECT_SLAB_Y_OFFSET_PX;
    }
    return out;
  }

  function createEllipseVertices(radiusX, radiusY, segments, tiltAngle) {
    var verts = [];
    var cosT = tiltAngle ? Math.cos(tiltAngle) : 1;
    var sinT = tiltAngle ? Math.sin(tiltAngle) : 0;
    for (var i = 0; i < segments; i++) {
      var angle = (i / segments) * Math.PI * 2;
      var x = radiusX * Math.cos(angle);
      var y = radiusY * Math.sin(angle);
      verts.push({ x: x * cosT - y * sinT, y: x * sinT + y * cosT });
    }
    return verts;
  }

  function degreesPerSecondToMatterSpawnSpin(degPerSec, frictionAir) {
    var Body = Matter.Body;
    var baseDelta = (Body && Body._baseDelta) ? Body._baseDelta : (1000 / 60);
    var radPerStep = (degPerSec * Math.PI / 180) * (baseDelta / 1000);
    var fa = isFinite(frictionAir) && frictionAir > 0 ? frictionAir : 0;
    if (fa > 0) {
      // Matter.js frictionAir dampens rotation; compensate so average spin ≈ target deg/s.
      radPerStep *= 1 + fa * 33.8;
    }
    return radPerStep;
  }

  function isFlaskWallLabel(label) {
    return label === "flask_wall" || label === "flask_neck" || label === "flask_mouth"
      || label === "flask_mouth_lid" || label === "flask_trapezoid_part";
  }

  /** Matter.js reports compound `flask_wall` hits on `flask_trapezoid_part` — map back to the root body. */
  function resolveFlaskWallRoot(walls, body) {
    if (!body) return body;
    var label = body.label;
    if (label === "flask_wall" || label === "flask_neck" || label === "flask_mouth"
      || label === "flask_mouth_lid") return body;
    if (label !== "flask_trapezoid_part" || !walls) return body;
    for (var i = 0; i < walls.length; i++) {
      var w = walls[i];
      if (w.label !== "flask_wall" || !w.parts) continue;
      for (var j = 0; j < w.parts.length; j++) {
        if (w.parts[j].id === body.id) return w;
      }
    }
    return body;
  }

  function buildFlaskWallIdSet(walls) {
    var set = new Set();
    if (!walls) return set;
    for (var i = 0; i < walls.length; i++) {
      var w = walls[i];
      set.add(w.id);
      if (w.parts) {
        for (var j = 0; j < w.parts.length; j++) {
          set.add(w.parts[j].id);
        }
      }
    }
    return set;
  }

  function getFlaskSideFromWall(flaskGeometry, wallBody) {
    if (!flaskGeometry || !wallBody || !wallBody.position) return 0;
    if (wallBody.label !== "flask_wall") return 0;
    var sideThreshold = Math.max(8, (flaskGeometry.W || 0) * 0.8);
    var wallDx = wallBody.position.x - flaskGeometry.cx;
    if (wallDx <= -sideThreshold) return -1;
    if (wallDx >= sideThreshold) return 1;
    return 0;
  }

  /**
   * FallPhysics
   *
   * @param {Object} opts
   *   runWidth, runHeight, evolutionChain, scale, runConfig, eventBus
   *   onBoulderHit(obj)  — called on first boulder-flask collision
   */
export function FallPhysics(opts) {
    this.opts = opts || {};
    this.runWidth = opts.runWidth || 800;
    this.runHeight = opts.runHeight || 600;
    this.evolutionChain = opts.evolutionChain || C.EVOLUTION_CHAIN || [];
    this.scale = opts.scale || null;
    this.runConfig = opts.runConfig || C.RUN_CONFIG || {};
    this.eventBus = opts.eventBus || EventBus || null;

    this.engine = null;
    this.world = null;
    this.flaskWalls = [];      // all static bodies for the flask shape
    this.flaskWallSet = null;  // Set of wall body ids for O(1) lookup
    this.flaskMouth = null;    // `flask_mouth` single rect; collect band is geometry-only
    this.collectSlab = null;   // always null — collect band geometry lives on flaskGeometry
    this.flaskCenterX = 0;     // tracks current flask center X
    this.flaskEnabled = true;
    // {cx, cy, sphereR, bodyTop, bodyBottom, neckHalfWidth, mouthY, neckLeft, neckRight, floorLeft, floorRight, T}
    this.flaskGeometry = null;

    // [{obj, body, suction: null | {startTime, frozenPos}, lastBounceAt}]
    this.bodies = [];
    // body.id -> entry (for fast collision lookup)
    this.bodiesMap = {};
  }

  // ---- flask geometry helpers ----

  FallPhysics.prototype._getFlaskDims = function (playerX) {
    var place = getRunwayFlaskPlacement(this.runHeight, this.scale);
    var cx = playerX != null ? playerX : this.runWidth / 2;
    return {
      flaskW: place.boxW,
      flaskH: place.boxH,
      flaskTopY: place.boxTopY,
      cx: cx,
    };
  };

  // ---- engine init ----

  FallPhysics.prototype._syncEngineGravity = function () {
    if (!this.engine) return;
    var gravity = getFallGravityForRunHeight(this.runHeight, this.runConfig);
    this.engine.gravity.y = gravity;
  };

  FallPhysics.prototype.ensureEngine = function (playerX) {
    if (this.engine) return;
    var gravity = getFallGravityForRunHeight(this.runHeight, this.runConfig);

    this.engine = Matter.Engine.create({
      gravity: { x: 0, y: gravity },
      positionIterations: 8,
      constraintIterations: 4,
    });
    this.world = this.engine.world;
    if (this.flaskEnabled) {
      this._buildFlaskBody(playerX != null ? playerX : this.runWidth / 2);
    }

    var self = this;
    Matter.Events.on(this.engine, "collisionStart", function (event) {
      self._onCollisionStart(event);
    });
  };

  // ---- flask body construction ----

  FallPhysics.prototype._buildFlaskBody = function (playerX) {
    var Bodies = Matter.Bodies;
    var Body = Matter.Body;
    var cx = playerX != null ? playerX : this.runWidth / 2;
    var W = this.scale && this.scale.flaskWallThickness ? this.scale.flaskWallThickness : DEFAULT_WALL_THICKNESS;
    var geom = buildRunwayFlaskPhysicsGeometry(cx, this.runHeight, this.scale, W, this.runConfig);

    this.flaskGeometry = Object.assign({}, geom);
    this.flaskCenterX = cx;

    var staticBase = {
      isStatic: true,
      isBullet: true,
      restitution: 0.6,
    };
    var built = createRunwayFlaskMatterColliders(Bodies, Body, geom, W, staticBase, {
      mouth: Object.assign({}, staticBase, {
        // Mouth is a trigger (sensor): rocks/debris pass through; HP + shatter use shell / mouth-lid.
        isSensor: true,
        collisionFilter: { category: CAT_FLASK_MOUTH, mask: MASK_FLASK_SHELL },
      }),
      neck: Object.assign({}, staticBase, {
        collisionFilter: { category: CAT_FLASK_SHELL, mask: MASK_FLASK_SHELL },
      }),
      shell: Object.assign({}, staticBase, {
        collisionFilter: { category: CAT_FLASK_SHELL, mask: MASK_FLASK_SHELL },
      }),
    });
    var walls = [];
    if (built.mouthBorder) {
      walls.push(built.mouthBorder);
      this.flaskMouth = built.mouthBorder;
    } else {
      this.flaskMouth = null;
    }
    if (built.neckSquare) walls.push(built.neckSquare);
    if (built.bodyTrapezoid) walls.push(built.bodyTrapezoid);

    var collectRc = collectSlabRunConfig(this.runConfig);
    var lidLayout = layoutFallMouthLid({
      cx: geom.cx,
      draw: {
        drawX: geom.drawX,
        drawY: geom.drawY,
        drawW: geom.drawW,
        drawH: geom.drawH,
      },
      wallThicknessPx: W,
      runConfig: collectRc,
    });
    walls.push(Bodies.rectangle(
      geom.cx,
      lidLayout.centerY,
      lidLayout.width,
      lidLayout.height,
      Object.assign({}, staticBase, {
        label: "flask_mouth_lid",
        collisionFilter: { category: CAT_FLASK_MOUTH, mask: MASK_FLASK_MOUTH },
      })
    ));

    var mouthHalfW = getFallMouthHalfWidthPx({
      flaskGeometry: geom,
      scale: this.scale,
      runWidth: this.runWidth,
      runHeight: this.runHeight,
      runConfig: this.runConfig,
      wallThicknessPx: W,
      cx: cx,
    }) || geom.neckHalfWidth;
    var collectLayout = layoutFallCollectSlab({
      cx: geom.cx,
      draw: {
        drawX: geom.drawX,
        drawY: geom.drawY,
        drawW: geom.drawW,
        drawH: geom.drawH,
      },
      wallThicknessPx: W,
      runConfig: collectRc,
    });
    this.flaskGeometry.mouthY = collectLayout.mouthY;
    this.flaskGeometry.collectSlabHalfHeight = collectLayout.halfHeight;
    this.flaskGeometry.collectSlabHalfWidth = collectLayout.halfWidth;
    this.flaskGeometry.collectTop = collectLayout.top;
    this.flaskGeometry.collectBottom = collectLayout.bottom;
    this.flaskGeometry.collectHalfWidth = collectLayout.halfWidth;
    this.flaskGeometry.collectLeft = geom.cx - collectLayout.halfWidth;
    this.flaskGeometry.collectRight = geom.cx + collectLayout.halfWidth;

    var mouthLayout = layoutFallMouth({
      cx: geom.cx,
      draw: {
        drawX: geom.drawX,
        drawY: geom.drawY,
        drawW: geom.drawW,
        drawH: geom.drawH,
      },
      wallThicknessPx: W,
      runConfig: collectRc,
    });
    this.flaskGeometry.mouthHalfHeight = mouthLayout.halfHeight;
    this.flaskGeometry.mouthHalfWidth = mouthLayout.halfWidth;

    this.flaskWalls = walls;
    this.flaskWallSet = buildFlaskWallIdSet(walls);
    Matter.World.add(this.world, walls);
    this.collectSlab = null;
  };

  // ---- flask movement ----

  FallPhysics.prototype.updateFlaskPosition = function (newPlayerX) {
    if (!this.engine || !this.flaskEnabled) return;
    var delta = newPlayerX - this.flaskCenterX;
    if (Math.abs(delta) < 0.5) return;

    var Body = Matter.Body;
    for (var i = 0; i < this.flaskWalls.length; i++) {
      var w = this.flaskWalls[i];
      Body.setPosition(w, { x: w.position.x + delta, y: w.position.y });
    }
    this.flaskCenterX = newPlayerX;
    if (this.flaskGeometry) {
      this.flaskGeometry.cx += delta;
    }
  };

  // ---- object body creation ----

  FallPhysics.prototype._applyFallObjectSpawnSpin = function (body) {
    if (!body) return;
    var rc = this.runConfig || {};
    var maxDegPerSec = (rc.FALL_OBJECT_SPAWN_SPIN_MAX_DEG_PER_S != null)
      ? Number(rc.FALL_OBJECT_SPAWN_SPIN_MAX_DEG_PER_S)
      : ((C.FALL_OBJECT_SPAWN_SPIN_MAX_DEG_PER_S != null)
        ? Number(C.FALL_OBJECT_SPAWN_SPIN_MAX_DEG_PER_S) : 60);
    if (!isFinite(maxDegPerSec) || maxDegPerSec <= 0) return;

    var Body = Matter.Body;
    var initialAngle = Math.random() * Math.PI * 2;
    if (Body && typeof Body.setAngle === "function") {
      Body.setAngle(body, initialAngle);
    } else {
      body.angle = initialAngle;
    }

    var frictionAir = body.frictionAir != null ? Number(body.frictionAir) : 0.01;
    var maxSpin = degreesPerSecondToMatterSpawnSpin(maxDegPerSec, frictionAir);
    var spawnSpin = (Math.random() * 2 - 1) * maxSpin;
    if (Body && typeof Body.setAngularVelocity === "function") {
      Body.setAngularVelocity(body, spawnSpin);
    } else {
      body.angularVelocity = spawnSpin;
    }
  };

  FallPhysics.prototype._createBody = function (obj, spawnX, spawnY) {
    var Bodies = Matter.Bodies;
    var type = obj.getType ? obj.getType() : (obj.type || "");
    var pixelW = fallObjectPixelWidth(obj.width, this.runWidth, this.runConfig);
    var avgRadius = Math.max(4, pixelW / 2);

    // Boulders collide with flask neck + body shell; collection is geometry-only (`checkSuction`).
    var opts = {
      restitution: 0.4,
      friction: 0.15,
      frictionAir: 0,
      density: 0.002,
      label: "fall_object",
      // Collectibles collide with mouth rim + neck/shell; collection is via `checkSuction`.
      collisionFilter: { category: CAT_OTHER_OBJ, mask: MASK_COLLECTIBLE_FLASK },
    };

    if (type === "boulder") {
      opts.collisionFilter = {
        category: CAT_BOULDER,
        mask: CAT_FLASK_SHELL | CAT_FLASK_MOUTH,
      };
      return Bodies.circle(spawnX, spawnY, avgRadius, opts);
    }

    if (type === "gem" || type === "bonus") {
      return Bodies.circle(spawnX, spawnY, avgRadius * 0.9, opts);
    }

    if (type === "merge_pickup") {
      var tier = obj.tier != null ? obj.tier : 0;
      var chain = this.evolutionChain;
      var tierCfg = (chain && chain[tier]) ? chain[tier] : {};
      var aspect = tierCfg.ellipseAspect != null ? tierCfg.ellipseAspect
        : (C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT != null
          ? C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT : 1.0);
      var tilt = tierCfg.ellipseAngle != null ? tierCfg.ellipseAngle
        : (C.MERGE_BODY_ELLIPSE_ANGLE_DEFAULT != null
          ? C.MERGE_BODY_ELLIPSE_ANGLE_DEFAULT : 0);

      if (Math.abs(aspect - 1) < 0.05) {
        return Bodies.circle(spawnX, spawnY, avgRadius, opts);
      }
      var rX = 2 * avgRadius * aspect / (1 + aspect);
      var rY = 2 * avgRadius / (1 + aspect);
      var verts = createEllipseVertices(rX, rY, 20, tilt);
      var body = Bodies.fromVertices(spawnX, spawnY, [verts], opts);
      return body || Bodies.circle(spawnX, spawnY, avgRadius, opts);
    }

    return Bodies.circle(spawnX, spawnY, avgRadius, opts);
  };

  // ---- spawn ----

  FallPhysics.prototype.spawnObject = function (obj) {
    this.ensureEngine();
    var pixelW = fallObjectPixelWidth(obj.width, this.runWidth, this.runConfig);
    var spawnX = obj.bottomCenterPosition * this.runWidth;
    var drawSize = getFallObjectDrawSize(obj, this.runWidth, this.runConfig);
    var cfg = this.runConfig || {};
    var appearMax = cfg.FALL_SPAWN_APPEAR_MAX_SCALE != null ? Number(cfg.FALL_SPAWN_APPEAR_MAX_SCALE) : 1.12;
    if (!isFinite(appearMax) || appearMax < 1) appearMax = 1.12;
    var margin = getFallSpawnTopMarginPx(this.runHeight, cfg);
    var spawnY = -(drawSize.halfExtent * appearMax) - margin;

    var body = this._createBody(obj, spawnX, spawnY);
    if (!body) return;

    var spawnType = obj.getType ? obj.getType() : (obj.type || "");
    if (spawnType === "boulder" || spawnType === "merge_pickup") {
      this._applyFallObjectSpawnSpin(body);
    }

    Matter.World.add(this.world, body);
    var spawnNow = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
    var particleMs = cfg.FALL_SPAWN_PARTICLE_MS != null ? Number(cfg.FALL_SPAWN_PARTICLE_MS) : 350;
    var entry = {
      obj: obj,
      body: body,
      suction: null,
      lastBounceAt: 0,
      sideSpinApplied: false,
      prevBottomY: null,
      spawnTime: spawnNow,
      spawnParticles: createFallSpawnBurstParticles(spawnX, spawnY, obj, this.runWidth, cfg, {
        seed: spawnNow + body.id,
      }),
      spawnParticlesStart: spawnNow,
      spawnParticlesUntil: spawnNow + Math.max(50, particleMs),
    };
    this.bodies.push(entry);
    this.bodiesMap[body.id] = entry;
  };

  // ---- collision handling ----

  FallPhysics.prototype._onCollisionStart = function (event) {
    var pairs = event.pairs;
    var now = this._frameTime != null ? this._frameTime
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i];

      var aIsWall = this.flaskWallSet && this.flaskWallSet.has(pair.bodyA.id);
      var bIsWall = this.flaskWallSet && this.flaskWallSet.has(pair.bodyB.id);
      if (!aIsWall && !bIsWall) continue;

      var wallCollider = aIsWall ? pair.bodyA : pair.bodyB;
      var wallBody = resolveFlaskWallRoot(this.flaskWalls, wallCollider);
      var dynBody = aIsWall ? pair.bodyB : pair.bodyA;
      var entry = this.bodiesMap[dynBody.id];
      if (!entry) continue;

      var obj = entry.obj;
      if (entry.suction || entry.exploding) continue;

      var type = obj.getType ? obj.getType() : (obj.type || "");

      // Letterbox mouth rim is a sensor (collect/debug pairing only); HP + shatter use shell / mouth-lid.
      if (wallBody && wallBody.label === "flask_mouth") continue;

      var wallSide = getFlaskSideFromWall(this.flaskGeometry, wallBody);

      // Debris fragments bounce and emit the same bounce cue as other objects.
      if (type === "boulder_debris") {
        this._applySideBounceSpin(dynBody, wallSide, entry);
        if (now - entry.lastBounceAt > BOUNCE_COOLDOWN_MS) {
          entry.lastBounceAt = now;
          if (this.eventBus) this.eventBus.emit("fall:bounce", { type: type });
        }
        continue;
      }

      if (type === "boulder") {
        var shieldActive = typeof this.opts.isShieldActive === "function" && this.opts.isShieldActive();
        var explodeForShield = false;
        // Damage on first flask contact (any wall), or any contact while shield is up
        if (!obj.hasDamaged) {
          obj.hasDamaged = true;
          if (typeof this.opts.onBoulderHit === "function") this.opts.onBoulderHit(obj);
          if (obj.shieldBlockedBoulderHit) explodeForShield = true;
        }
        // Mouth-lid hit, or any hit that just broke the shield → shatter into debris
        if (wallBody.label === "flask_mouth_lid" || explodeForShield) {
          var debrisY = null;
          if (wallBody.label === "flask_mouth_lid"
            && this.flaskGeometry
            && this.flaskGeometry.T != null) {
            debrisY = this.flaskGeometry.T;
          } else if (dynBody && dynBody.position) {
            debrisY = dynBody.position.y;
          }
          this._explodeBoulderEntry(entry, { debrisY: debrisY });
        }
        continue;
      }

      // Non-boulder wall hit (gems, shields, merge pickups, leftover types): bounce SFX +
      // first-hit spin only. Collection is via `checkSuction` at the rim band.
      this._applySideBounceSpin(dynBody, wallSide, entry);
      if (now - entry.lastBounceAt > BOUNCE_COOLDOWN_MS) {
        entry.lastBounceAt = now;
        if (this.eventBus) this.eventBus.emit("fall:bounce", { type: type });
      }
    }
  };

  /**
   * Freeze a collectible entry and start its suction animation. Used by per-frame
   * swept `checkSuction`. No-op for boulders,
   * debris, already-suctioning/exploding, or already-collected entries.
   * @returns {boolean} true when suction was newly started.
   */
  FallPhysics.prototype._beginSuction = function (entry, currentTime) {
    if (!entry || entry.suction || entry.exploding) return false;
    var obj = entry.obj;
    if (!obj || obj.collected || obj.caught) return false;
    var type = obj.getType ? obj.getType() : (obj.type || "");
    if (type === "boulder" || type === "boulder_debris") return false;
    var Body = Matter.Body;
    Body.setStatic(entry.body, true);
    var pos = entry.body.position;
    entry.suction = { startTime: currentTime, frozenPos: { x: pos.x, y: pos.y } };
    if (this.eventBus) this.eventBus.emit("fall:suction", { type: type, tier: obj.tier });
    return true;
  };

  FallPhysics.prototype._applySideBounceSpin = function (body, side, entry) {
    if (!body || !side) return;
    if (entry && entry.sideSpinApplied) return;
    var rc = this.runConfig || {};
    var configuredSpin = (rc.FALL_SIDE_BOUNCE_SPIN != null)
      ? Number(rc.FALL_SIDE_BOUNCE_SPIN)
      : ((C.FALL_SIDE_BOUNCE_SPIN != null)
        ? Number(C.FALL_SIDE_BOUNCE_SPIN) : 0.16);
    if (!(configuredSpin > 0)) return;
    // Opposite mapping: left wall imparts negative spin, right wall positive.
    var impulse = Math.abs(configuredSpin) * (side < 0 ? -1 : 1);
    var currentSpin = body.angularVelocity || 0;
    var nextSpin = currentSpin + impulse;
    var maxSpin = Math.max(0.32, Math.abs(configuredSpin) * 4);
    if (nextSpin > maxSpin) nextSpin = maxSpin;
    if (nextSpin < -maxSpin) nextSpin = -maxSpin;
    if (Matter.Body && typeof Matter.Body.setAngularVelocity === "function") {
      Matter.Body.setAngularVelocity(body, nextSpin);
    } else {
      body.angularVelocity = nextSpin;
    }
    if (entry) entry.sideSpinApplied = true;
  };

  // ---- suction zone ----

  FallPhysics.prototype.getMouthInfo = function () {
    var geom = this.flaskGeometry;
    if (!geom) return null;
    var mouthHalfW = getFallMouthHalfWidthPx({
      flaskGeometry: geom,
      scale: this.scale,
      runWidth: this.runWidth,
      runHeight: this.runHeight,
      runConfig: this.runConfig,
      wallThicknessPx: geom.W,
      cx: geom.cx,
    });
    if (mouthHalfW == null && geom.collectSlabHalfWidth != null) {
      mouthHalfW = geom.collectSlabHalfWidth;
    }
    if (mouthHalfW == null) mouthHalfW = geom.neckHalfWidth;
    return { x: geom.cx, y: geom.mouthY, halfWidth: mouthHalfW };
  };

  /**
   * Collect band at the mouth entrance: horizontal span [x ± halfWidth] and vertical
   * band [top, bottom] centred on the mouth line. Stored on `flaskGeometry` in
   * `_buildFlaskBody`; usable without a live Matter collect body.
   */
  FallPhysics.prototype.getCollectSlabInfo = function () {
    var geom = this.flaskGeometry;
    if (!geom) return null;
    if (geom.collectSlabHalfHeight != null && geom.mouthY != null && geom.collectSlabHalfWidth != null) {
      return {
        x: geom.cx, y: geom.mouthY, halfWidth: geom.collectSlabHalfWidth,
        top: geom.mouthY - geom.collectSlabHalfHeight,
        bottom: geom.mouthY + geom.collectSlabHalfHeight,
      };
    }
    var mouth = this.getMouthInfo();
    if (!mouth) return null;
    var geom = this.flaskGeometry;
    if (geom && geom.drawW > 0 && geom.drawH > 0) {
      var slabLayout = layoutFallCollectSlab({
        cx: geom.cx,
        draw: {
          drawX: geom.drawX,
          drawY: geom.drawY,
          drawW: geom.drawW,
          drawH: geom.drawH,
        },
        wallThicknessPx: geom.W,
        runConfig: collectSlabRunConfig(this.runConfig),
      });
      return {
        x: geom.cx,
        y: slabLayout.mouthY,
        halfWidth: slabLayout.halfWidth,
        top: slabLayout.top,
        bottom: slabLayout.bottom,
      };
    }
    return {
      x: mouth.x, y: mouth.y, halfWidth: mouth.halfWidth,
      top: mouth.y - (geom.collectSlabHalfHeight || 6),
      bottom: mouth.y + (geom.collectSlabHalfHeight || 6),
    };
  };

  /**
   * checkSuction — call each frame before the physics step.
   *
   * Collection happens only at the mouth collect band. An object is collected
   * when it overlaps the slab horizontally by >=50% of its own width AND its vertical
   * travel intersects the slab band. To avoid fast objects tunnelling through the thin
   * slab, this uses interpolated (swept) detection: the segment between the object's
   * previous-frame bottom edge and this-frame bottom edge is tested against the band,
   * in addition to the direct band overlap. Returns newly-sucked entries.
   */
  FallPhysics.prototype.checkSuction = function (currentTime) {
    var slab = this.getCollectSlabInfo();
    if (!slab) return [];
    var slabTuning = getFallCollectSlabTuning(collectSlabRunConfig(this.runConfig));

    var sucked = [];
    for (var i = 0; i < this.bodies.length; i++) {
      var entry = this.bodies[i];
      if (entry.suction || entry.exploding) continue;
      var obj = entry.obj;
      if (obj.collected || obj.caught) continue;
      var type = obj.getType ? obj.getType() : (obj.type || "");
      if (type === "boulder" || type === "boulder_debris") continue;

      var bounds = entry.body.bounds;
      var currBottom = bounds.max.y;

      if (fallCollectSlabQualifiesForCollection({
        slab: slab,
        bounds: bounds,
        prevBottom: entry.prevBottomY,
      }, slabTuning)) {
        if (this._beginSuction(entry, currentTime)) sucked.push(entry);
        continue;
      }
      entry.prevBottomY = currBottom;
    }
    return sucked;
  };

  /**
   * processSuction — call each frame after physics step.
   * Removes bodies whose suction animation has finished.
   * Returns an array of completed entries (ready to be collected).
   */
  FallPhysics.prototype.processSuction = function (currentTime) {
    var animEnabled = C.FALL_SUCTION_ANIMATION_ENABLED != null
      ? C.FALL_SUCTION_ANIMATION_ENABLED : true;
    var ms = (!animEnabled) ? 0
      : (C.FALL_SUCTION_ANIMATION_MS != null
        ? C.FALL_SUCTION_ANIMATION_MS : DEFAULT_SUCTION_MS);

    var finished = [];
    for (var i = this.bodies.length - 1; i >= 0; i--) {
      var entry = this.bodies[i];
      if (!entry.suction) continue;
      if (currentTime - entry.suction.startTime >= ms) {
        Matter.World.remove(this.world, entry.body);
        delete this.bodiesMap[entry.body.id];
        this.bodies.splice(i, 1);
        finished.push(entry);
      }
    }
    return finished;
  };

  /**
   * Freeze the boulder body, spawn debris, and queue removal via processExplosions.
   * Used for mouth-lid hits and for boulders that break an active shield.
   * @param {{obj: object, body: object, exploding?: boolean}} entry
   * @param {{debrisY?: number}} [opts]
   */
  FallPhysics.prototype._explodeBoulderEntry = function (entry, opts) {
    if (!entry || entry.exploding || !entry.body || !entry.obj) return;
    opts = opts || {};
    var dynBody = entry.body;
    var obj = entry.obj;
    var pos = dynBody.position || { x: 0, y: 0 };
    var pixelW = fallObjectPixelWidth(obj.width, this.runWidth, this.runConfig);
    var debrisY = opts.debrisY != null && isFinite(opts.debrisY)
      ? opts.debrisY
      : (this.flaskGeometry && this.flaskGeometry.T != null ? this.flaskGeometry.T : pos.y);
    Matter.Body.setStatic(dynBody, true);
    entry.exploding = true;
    this._spawnDebris(pos.x, debrisY, Math.max(6, pixelW / 2));
    if (this.eventBus) {
      this.eventBus.emit("fall:boulder_explode", {
        obj: obj,
        shieldBlocked: !!obj.shieldBlockedBoulderHit,
      });
    }
  };

  /**
   * processExplosions — call each frame after physics step.
   * Removes the frozen original boulder body on the frame after _spawnDebris ran.
   * Debris fragments are independent physics bodies and are pruned off-screen normally.
   */
  FallPhysics.prototype.processExplosions = function (currentTime) {
    for (var i = this.bodies.length - 1; i >= 0; i--) {
      var entry = this.bodies[i];
      if (!entry.exploding) continue;
      Matter.World.remove(this.world, entry.body);
      delete this.bodiesMap[entry.body.id];
      this.bodies.splice(i, 1);
    }
  };

  /**
   * _spawnDebris — called when a boulder hits the flask mouth-lid.
   * Spawns 4–7 small physics fragments that fly upward with random sideways lean,
   * then fall off-screen under gravity. Each fragment renders as a scaled-down
   * boulder image (same asset as RunBoulder).
   * Debris collide with flask walls and the mouth-lid but never damage the flask.
   */
  FallPhysics.prototype._spawnDebris = function (cx, mouthY, originalRadius) {
    var count = 4 + Math.floor(Math.random() * 4); // 4–7
    var Bodies = Matter.Bodies;
    // Spread: all fragments fly upward (−π/2) ± up to 75° sideways
    var HALF_SPREAD = Math.PI * 5 / 12; // 75°
    for (var i = 0; i < count; i++) {
      var debrisRadius = Math.max(4, originalRadius * (0.25 + Math.random() * 0.2));
      var debrisWidth = (debrisRadius * 2) / this.runWidth;

      // Each fragment picks a random upward angle within the spread cone
      var angle = -Math.PI / 2 + (Math.random() * 2 - 1) * HALF_SPREAD;
      var speed = 2 + Math.random() * 5; // px per physics step
      var vx = Math.cos(angle) * speed;
      var vy = Math.sin(angle) * speed; // always negative (upward) within spread cone

      var debrisOpts = {
        restitution: 0.55,
        friction: 0.1,
        frictionAir: 0,
        isBullet: false,
        density: 0.0015,
        label: "fall_object",
        collisionFilter: { category: CAT_DEBRIS, mask: CAT_FLASK_SHELL | CAT_FLASK_MOUTH },
      };

      var body = Bodies.circle(cx, mouthY, debrisRadius, debrisOpts);
      Matter.World.add(this.world, body);
      Matter.Body.setVelocity(body, { x: vx, y: vy });
      var rc = this.runConfig || {};
      var debrisSpinMax = (rc.FALL_DEBRIS_SPAWN_SPIN_MAX != null)
        ? Number(rc.FALL_DEBRIS_SPAWN_SPIN_MAX)
        : ((C.FALL_DEBRIS_SPAWN_SPIN_MAX != null)
          ? Number(C.FALL_DEBRIS_SPAWN_SPIN_MAX) : 0.25);
      if (debrisSpinMax > 0) {
        var spawnSpin = (Math.random() * 2 - 1) * debrisSpinMax;
        if (Matter.Body && typeof Matter.Body.setAngularVelocity === "function") {
          Matter.Body.setAngularVelocity(body, spawnSpin);
        } else {
          body.angularVelocity = spawnSpin;
        }
      }

      var debrisObj = (function (w) {
        return {
          getType: function () { return "boulder_debris"; },
          width: w,
          getAspectRatio: function () { return 1.0; },
          getDrawImage: function () { return _getDebrisImg(); },
          draw: function (ctx, x, y, pw, ph) {
            ctx.save();
            var debrisImg = _getDebrisImg();
            if (debrisImg) {
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = "high";
              ctx.drawImage(debrisImg, x, y, pw, ph);
            } else {
              // Fallback: plain brown circle until image loads
              ctx.fillStyle = "#7c6b4f";
              ctx.beginPath();
              ctx.arc(x + pw / 2, y + ph / 2, Math.min(pw, ph) / 2, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.restore();
          },
          collected: false,
          caught: false,
        };
      })(debrisWidth);

      var debrisEntry = { obj: debrisObj, body: body, suction: null, exploding: null, lastBounceAt: 0, sideSpinApplied: false };
      this.bodies.push(debrisEntry);
      this.bodiesMap[body.id] = debrisEntry;
    }
  };

  /**
   * True when no runway objects, suction, spawn bursts, or explosion cleanup remain visible.
   * @param {number} [currentTime]
   */
  FallPhysics.prototype.isRunwayVisuallyIdle = function (currentTime) {
    var now = currentTime != null ? currentTime
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    for (var i = 0; i < this.bodies.length; i++) {
      var entry = this.bodies[i];
      if (entry.spawnParticlesUntil != null && now < entry.spawnParticlesUntil) return false;
      var obj = entry.obj;
      if (obj && (obj.collected || obj.caught)) continue;
      if (entry.suction || entry.exploding) return false;
      return false;
    }
    return true;
  };

  // ---- off-screen pruning ----

  FallPhysics.prototype.pruneOffscreen = function () {
    var extraBelow = computeFallDrawTopOverflowPx(this.runWidth, this.runConfig, this.runHeight);
    var limit = this.runHeight + Math.max(300, extraBelow + 200);
    var pruned = [];
    for (var i = this.bodies.length - 1; i >= 0; i--) {
      var entry = this.bodies[i];
      if (entry.body.position.y > limit) {
        Matter.World.remove(this.world, entry.body);
        delete this.bodiesMap[entry.body.id];
        this.bodies.splice(i, 1);
        pruned.push(entry);
      }
    }
    return pruned;
  };

  /**
   * Remove live fall objects of a given type (e.g. unused shield pickups).
   * Skips entries mid-suction so an in-progress collect can finish.
   * @param {string} type
   * @returns {number} Count removed
   */
  FallPhysics.prototype.removeObjectsByType = function (type) {
    if (!type || !this.world) return 0;
    var removed = 0;
    for (var i = this.bodies.length - 1; i >= 0; i--) {
      var entry = this.bodies[i];
      if (!entry || entry.suction || entry.exploding) continue;
      var obj = entry.obj;
      var objType = obj && obj.getType ? obj.getType() : (obj && obj.type) || "";
      if (objType !== type) continue;
      Matter.World.remove(this.world, entry.body);
      delete this.bodiesMap[entry.body.id];
      this.bodies.splice(i, 1);
      removed++;
    }
    return removed;
  };

  // ---- physics step ----

  FallPhysics.prototype.step = function (deltaMs, currentTime) {
    // Store frame time so collision handlers (which fire during Engine.update) use
    // the same epoch as checkSuction / processSuction / drawObjects (performance.now).
    this._frameTime = currentTime != null ? currentTime
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (!this.engine) return;
    var safeDelta = Number(deltaMs);
    if (!isFinite(safeDelta) || safeDelta <= 0) return;
    var rc = this.runConfig || {};
    var maxFrameMs = (rc.MAX_FRAME_DELTA_MS != null)
      ? Number(rc.MAX_FRAME_DELTA_MS)
      : ((C.MAX_FRAME_DELTA_MS != null)
        ? Number(C.MAX_FRAME_DELTA_MS) : DEFAULT_MAX_FRAME_MS);
    var clampedDelta = Math.min(maxFrameMs, safeDelta);
    var fixedStepMs = (rc.FALL_PHYSICS_STEP_MS != null)
      ? Number(rc.FALL_PHYSICS_STEP_MS)
      : ((C.FALL_PHYSICS_STEP_MS != null)
        ? Number(C.FALL_PHYSICS_STEP_MS) : DEFAULT_PHYSICS_STEP_MS);
    var maxSubsteps = (rc.FALL_PHYSICS_MAX_SUBSTEPS != null)
      ? Number(rc.FALL_PHYSICS_MAX_SUBSTEPS)
      : ((C.FALL_PHYSICS_MAX_SUBSTEPS != null)
        ? Number(C.FALL_PHYSICS_MAX_SUBSTEPS) : DEFAULT_MAX_SUBSTEPS);
    var substeps = Math.max(1, Math.min(maxSubsteps, Math.ceil(clampedDelta / fixedStepMs)));
    var stepMs = clampedDelta / substeps;
    for (var i = 0; i < substeps; i++) {
      Matter.Engine.update(this.engine, stepMs);
    }
  };

  // ---- resize ----

  FallPhysics.prototype.resize = function (runWidth, runHeight, playerX) {
    this.runWidth = runWidth;
    this.runHeight = runHeight;
    this._syncEngineGravity();
    if (!this.engine) return;
    // Rebuild flask walls at new position
    var World = Matter.World;
    for (var i = 0; i < this.flaskWalls.length; i++) {
      World.remove(this.world, this.flaskWalls[i]);
    }
    if (this.collectSlab) {
      World.remove(this.world, this.collectSlab);
      this.collectSlab = null;
    }
    this.flaskWalls = [];
    this.flaskWallSet = new Set();
    if (this.flaskEnabled) {
      this._buildFlaskBody(playerX != null ? playerX : this.runWidth / 2);
    } else {
      this.flaskGeometry = null;
    }
  };

  FallPhysics.prototype.disableFlask = function () {
    this.flaskEnabled = false;
    if (!this.engine || !this.world) {
      this.flaskWalls = [];
      this.flaskWallSet = new Set();
      this.flaskGeometry = null;
      return;
    }
    for (var i = 0; i < this.flaskWalls.length; i++) {
      Matter.World.remove(this.world, this.flaskWalls[i]);
    }
    this.flaskWalls = [];
    this.flaskWallSet = new Set();
    this.flaskGeometry = null;
  };

  FallPhysics.prototype.enableFlask = function (playerX) {
    if (this.flaskEnabled) return;
    this.flaskEnabled = true;
    if (!this.engine || !this.world) return;
    this._buildFlaskBody(playerX != null ? playerX : this.flaskCenterX);
  };

  // ---- cleanup ----

  FallPhysics.prototype.destroy = function () {
    if (this.engine) {
      Matter.Events.off(this.engine);
      Matter.World.clear(this.world, false);
      Matter.Engine.clear(this.engine);
      this.engine = null;
      this.world = null;
    }
    this.bodies = [];
    this.bodiesMap = {};
    this.flaskWalls = [];
    this.flaskWallSet = null;
  };